import type {Subject} from '@github-ui/comment-box/subject'
import styles from '@github-ui/commenting/AvatarStyles.module.css'
import {getQuotedText} from '@github-ui/commenting/quotes'
import {RefreshVideoWrapper} from '@github-ui/commenting/RefreshVideoWrapper'
import {dynamic} from '@github-ui/dynamic'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {GitHubAvatar} from '@github-ui/github-avatar'
import {useGetHovercardAttributesForType} from '@github-ui/hovercards/use-get-hovercard-attributes-for-type'
import {hpcAttrs} from '@github-ui/hpc'
import {canBeOptimisticallyParsed} from '@github-ui/md2html'
import type {SafeHTMLString} from '@github-ui/safe-html'
import type {RelayCompat} from '@github-ui/server-persisted-queries/relay-compat'
import type {IssueViewerSecondaryViewQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerSecondaryViewQuery'
import type {IssueViewerViewQueryQuery} from '@github-ui/server-persisted-queries/types/IssueViewerViewQuery'
import {SIDE_PANEL_PORTAL_ROOT} from '@github-ui/side-panel'
// eslint-disable-next-line no-restricted-imports
import {useToastContext} from '@github-ui/toast/ToastContext'
import {useSessionStorage} from '@github-ui/use-safe-storage/session-storage'
import {useShouldShowCopilotFunctionalityInIssues} from '@github-ui/use-should-show-copilot-functionality-in-issues'
import {PencilIcon} from '@primer/octicons-react'
import {IconButton, Link} from '@primer/react'
import {clsx} from 'clsx'
import {type ReactNode, Suspense, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {graphql, useRelayEnvironment} from 'react-relay'

import type {IssueBodyRefetchQuery, IssueBodyRefetchQuery$data} from './__generated__/IssueBodyRefetchQuery.graphql'
import {
  IssueBodyHeader,
  type IssueBodyHeaderSecondaryFragment,
  type IssueBodyHeaderSecondaryFragment_issueFragment,
} from './components/IssueBodyHeader'
import {IssueBodyViewer, type IssueBodyViewerPinnedCommentSecondaryFragment} from './components/IssueBodyViewer'
import {ERRORS} from './constants/errors'
import {LABELS} from './constants/labels'
import {TEST_IDS} from './constants/test-ids'
import {VALUES} from './constants/values'
import issueBodyStyles from './IssueBody.module.css'
import type {updateIssueBodyMutation$data} from './mutations/__generated__/updateIssueBodyMutation.graphql'
import {commitUpdateIssueBodyMutation} from './mutations/update-issue-body-mutation'

const IssueBodyEditor = dynamic(
  async () => {
    const m = await import('./components/IssueBodyEditor')
    return {default: m.IssueBodyEditor}
  },
  {displayName: 'IssueBodyEditor'},
)

type IssueSecondaryNode = NonNullable<NonNullable<IssueViewerSecondaryViewQueryQuery['repository']>['issue']>
type IssueBodySecondaryFragment = IssueBodyViewerPinnedCommentSecondaryFragment &
  RelayCompat<
    Pick<
      IssueSecondaryNode,
      | 'viewerCanReport'
      | 'viewerCanReportToMaintainer'
      | 'viewerCanBlockFromOrg'
      | 'viewerCanUnblockFromOrg'
      | 'viewerCanBlockPersonally'
      | 'viewerCanUnblockPersonally'
    > & {
      repository: Pick<IssueSecondaryNode['repository'], 'viewerCanCreateIssues'>
    }
  >

export type IssueBodySecondaryHeaderFragment = IssueBodyHeaderSecondaryFragment &
  IssueBodyHeaderSecondaryFragment_issueFragment

type IssueViewerViewIssueNode = NonNullable<NonNullable<IssueViewerViewQueryQuery['repository']>['issue']>

// RELAY-CLEANUP(IssueViewerIssue): widens nullable fields so the still-Relay IssueViewerIssue data
// is assignable. Remove when IssueViewerIssue leaves Relay.
export type IssueBodyFragment = RelayCompat<
  Pick<
    IssueViewerViewIssueNode,
    | 'id'
    | 'databaseId'
    | 'title'
    | 'createdAt'
    | 'viewerDidAuthor'
    | 'locked'
    | 'author'
    | 'url'
    | 'viewerCanUpdateNext'
    | 'number'
    | 'viewerCanUpdateMetadata'
    | 'subIssuesSummary'
    | 'body'
    | 'bodyHTML'
    | 'bodyVersion'
    | 'reactionGroups'
    | 'pinnedIssueComment'
  > & {
    readonly repository: Pick<
      IssueViewerViewIssueNode['repository'],
      | 'databaseId'
      | 'id'
      | 'name'
      | 'nameWithOwner'
      | 'isArchived'
      | 'slashCommandsEnabled'
      | 'visibility'
      | 'viewerCanPush'
    > & {
      readonly owner: Pick<IssueViewerViewIssueNode['repository']['owner'], 'id' | 'login' | 'url'>
    }
    // pendingBlock/pendingUnblock are in the Relay schema but not yet in the SPQ
    readonly pendingBlock: boolean | null
    readonly pendingUnblock: boolean | null
  }
>

type IssueBodyProps = {
  onLinkClick?: (event: MouseEvent) => void
  onIssueUpdate?: () => void
  onIssueEditStateChange?: (isEditing: boolean) => void
  isIssueEditActive?: () => boolean
  onCommentReply: (quotedComment: string) => void
  highlightedEventText?: string
  issue: IssueBodyFragment
  insideSidePanel?: boolean
  isReadonly: boolean
  issueBodySecondary?: IssueBodySecondaryFragment
  secondaryKey?: IssueBodySecondaryHeaderFragment
  issueBodyTaskListAction?: ReactNode
}

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const IssueBodyRefetchQuery = graphql`
  query IssueBodyRefetchQuery($id: ID!) {
    node(id: $id) {
      ... on Issue {
        bodyHTML(unfurlReferences: true)
      }
    }
  }
`

export function IssueBody({
  issue: data,
  issueBodySecondary: secondaryData,
  secondaryKey,
  onLinkClick,
  onIssueEditStateChange,
  onIssueUpdate,
  isIssueEditActive,
  onCommentReply,
  highlightedEventText,
  insideSidePanel,
  isReadonly,
  issueBodyTaskListAction,
}: IssueBodyProps) {
  const {body: issueBody, bodyHTML, bodyVersion} = data
  const issueId = data.id
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const repositoryDatabaseId = data.repository.databaseId!

  const [presavedBody, setPresavedBody] = useSessionStorage<string | undefined>(
    VALUES.localStorageKeys.issueNewBody('hyperlist', issueId),
    undefined,
  )

  const [currentIssueBodyVersion, setCurrentIssueBodyVersion] = useState<string>(bodyVersion)
  const [bodyIsStale, setBodyIsStale] = useState(false)
  const [markdownBody, setMarkdownBody] = useState<string>(presavedBody || issueBody)
  const [isIssueBodyEditActive, setIsIssueBodyEditActive] = useState(false)
  const [hasDisabledActions, setHasDisabledActions] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasValidationError, setHasValidationError] = useState(false)

  const [defferedIsEditing, setDefferedIsEditing] = useState(false)
  const relayEnvironment = useRelayEnvironment()

  const issueBodyRef = useRef<HTMLDivElement>(null)

  const {addToast} = useToastContext()

  const onBodyChange = useCallback(
    (newMarkdown: string) => {
      setMarkdownBody(newMarkdown)
      setPresavedBody(newMarkdown)
      onIssueEditStateChange?.(true)
    },
    [onIssueEditStateChange, setPresavedBody],
  )

  useEffect(() => {
    // If the body version changes **after** the user started editing, keep using the previous body version
    if (defferedIsEditing) {
      return
    }
    // If the body version changes **before** the user started editing, update the current body version
    if (bodyVersion !== currentIssueBodyVersion) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCurrentIssueBodyVersion(bodyVersion)
    }
  }, [bodyVersion, currentIssueBodyVersion, defferedIsEditing])

  useEffect(() => {
    if (!isIssueEditActive?.()) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setIsIssueBodyEditActive(false)
    }
  }, [isIssueEditActive, setIsIssueBodyEditActive])

  const subject = useMemo<Subject>(() => {
    return {
      type: 'issue',
      id: {
        id: data.id,
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        databaseId: data.databaseId!,
      },
      repository: {
        databaseId: repositoryDatabaseId,
        nwo: data.repository.nameWithOwner,
        slashCommandsEnabled: data.repository.slashCommandsEnabled,
      },
    }
  }, [
    data.databaseId,
    data.id,
    data.repository.nameWithOwner,
    data.repository.slashCommandsEnabled,
    repositoryDatabaseId,
  ])

  useEffect(() => {
    if (isSubmitting) return

    if (isIssueBodyEditActive) {
      return
    }

    // Don't reset markdown body if we have a validation error (error recovery case)
    if (!hasValidationError) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMarkdownBody(issueBody)
    }

    return () => {
      setBodyIsStale(false)
      onIssueEditStateChange?.(false)
    }
  }, [isIssueBodyEditActive, isSubmitting, issueBody, onIssueEditStateChange, hasValidationError])

  // eslint-disable-next-line react-hooks/set-state-in-effect
  useEffect(() => setIsIssueBodyEditActive(false), [issueId, setIsIssueBodyEditActive])

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDefferedIsEditing(isIssueBodyEditActive)
  }, [isIssueBodyEditActive])

  const commitIssueBodyEdit = useCallback(() => {
    const body = presavedBody || markdownBody
    const withOptimisticResponse = canBeOptimisticallyParsed(body)
    if (withOptimisticResponse) {
      setIsIssueBodyEditActive(false)
      setHasDisabledActions(true)
    } else {
      setIsSubmitting(true)
    }
    commitUpdateIssueBodyMutation({
      environment: relayEnvironment,
      input: {issueId, body, bodyVersion: currentIssueBodyVersion},
      onError: (error: Error) => {
        setHasDisabledActions(false)
        // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
        addToast({
          type: 'error',
          message: error.message.startsWith(LABELS.staleBodyErrorPrefix)
            ? ERRORS.couldNotUpdateIssueBodyStale
            : ERRORS.couldNotUpdateIssueBody,
        })

        if (error.message.startsWith(LABELS.staleBodyErrorPrefix)) {
          setIsIssueBodyEditActive(true)
          setIsSubmitting(false)
        } else {
          onIssueEditStateChange?.(false)
        }
        setHasValidationError(true)
      },
      onCompleted: (response: updateIssueBodyMutation$data) => {
        setHasDisabledActions(false)
        if (!response.updateIssue) {
          // eslint-disable-next-line @github-ui/dotcom-primer/toast-migration
          addToast({
            type: 'error',
            message: ERRORS.couldNotUpdateIssueBody,
          })

          setIsIssueBodyEditActive(true)
          setIsSubmitting(false)
          setHasValidationError(true)
        } else {
          setHasValidationError(false)
          onIssueUpdate?.()
          setIsIssueBodyEditActive(false)
          onIssueEditStateChange?.(false)
          setIsSubmitting(false)
          if (response.updateIssue?.issue) {
            setCurrentIssueBodyVersion(response.updateIssue.issue.bodyVersion)
          }
          setPresavedBody(undefined)
        }
      },
      withOptimisticResponse,
    })
  }, [
    relayEnvironment,
    issueId,
    presavedBody,
    markdownBody,
    currentIssueBodyVersion,
    addToast,
    onIssueEditStateChange,
    onIssueUpdate,
    setPresavedBody,
  ])

  const handleOnReply = (quotedText?: string) => {
    onCommentReply(quotedText || getQuotedText(issueBody))
  }

  const highlighted = useMemo(() => {
    if (!highlightedEventText) return false
    return highlightedEventText === `#issue-${data.databaseId}`
  }, [highlightedEventText, data.databaseId])

  const url = `${data.url}#issue-${data.databaseId}`

  const {avatarUrl, login, profileUrl} = data.author ?? VALUES.ghost

  const isCopilotOrAgent = data.author?.__typename === 'Bot' && (data.author?.isCopilot || data.author?.isAgent)
  const isNonAgentBot = data.author?.__typename === 'Bot' && !isCopilotOrAgent

  const getHovercardAttributesForType = useGetHovercardAttributesForType()

  let authorHovercardAttrs = {}
  if (isCopilotOrAgent) {
    authorHovercardAttrs = getHovercardAttributesForType('copilot', {botSlug: login})
  } else if (!isNonAgentBot && profileUrl) {
    authorHovercardAttrs = getHovercardAttributesForType('user', {login})
  }

  const shouldShowCopilotFunctionality = useShouldShowCopilotFunctionalityInIssues({
    repositoryVisibility: data.repository.visibility,
    userCanPushToRepo: data.repository.viewerCanPush,
  })

  const modernUx = isFeatureEnabled('issues_modern_ux')

  const getHTML = useCallback((fetchResult: Awaited<IssueBodyRefetchQuery$data> | undefined): string | undefined => {
    return fetchResult?.node?.bodyHTML
  }, [])

  const inlineAvatars = isFeatureEnabled('issue_inline_avatars')

  if (modernUx) {
    return (
      <div className={issueBodyStyles.modernUxContainer}>
        <div
          data-testid={TEST_IDS.issueBody}
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          className={clsx('react-issue-body', issueBodyStyles.modernUxBody, highlighted && issueBodyStyles.highlighted)}
          {...hpcAttrs()}
        >
          <h2 className="sr-only">{LABELS.issueBodyHeader}</h2>
          {defferedIsEditing || isSubmitting || hasValidationError ? (
            <div className={issueBodyStyles.editorWrapper}>
              <Suspense fallback={null}>
                <IssueBodyEditor
                  editorDisabled={isSubmitting}
                  trailingIcon={!isSubmitting}
                  subjectId={issueId}
                  subject={subject}
                  body={hasValidationError ? markdownBody : presavedBody || markdownBody}
                  bodyIsStale={bodyIsStale}
                  onChange={onBodyChange}
                  onCancel={() => {
                    setIsIssueBodyEditActive(false)
                    setMarkdownBody(issueBody)
                    setPresavedBody(undefined)
                    onIssueEditStateChange?.(false)
                    setCurrentIssueBodyVersion(bodyVersion)
                  }}
                  onCommit={() => {
                    commitIssueBodyEdit()
                  }}
                  repoOwner={data.repository.owner.login}
                  repoName={data.repository.name}
                  title={data.title}
                  shouldShowCopilotFunctionality={shouldShowCopilotFunctionality}
                />
              </Suspense>
            </div>
          ) : (
            <div className={issueBodyStyles.modernUxViewerWrapper}>
              {!isReadonly && data.viewerCanUpdateNext && (
                <div className={issueBodyStyles.modernUxEditButton}>
                  <IconButton
                    icon={PencilIcon}
                    aria-label="Edit issue body"
                    variant="invisible"
                    size="small"
                    onClick={() => setIsIssueBodyEditActive(true)}
                    onMouseEnter={IssueBodyEditor.preload}
                  />
                </div>
              )}
              <RefreshVideoWrapper<IssueBodyRefetchQuery>
                /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. */
                bodyHTML={bodyHTML as SafeHTMLString}
                bodyRef={issueBodyRef}
                id={issueId}
                query={IssueBodyRefetchQuery}
                getHTML={getHTML}
              >
                <IssueBodyViewer
                  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. */
                  html={bodyHTML as SafeHTMLString}
                  markdown={issueBody}
                  comment={data}
                  onLinkClick={onLinkClick}
                  issueBodyRef={issueBodyRef}
                  bodyVersion={currentIssueBodyVersion}
                  locked={data.locked}
                  reactable={data}
                  subIssues={data}
                  pinnedComment={data}
                  pinnedCommentSecondary={secondaryData}
                  insideSidePanel={insideSidePanel}
                  repositoryId={data.repository.id}
                  onIssueEditStateChange={onIssueEditStateChange}
                  isReadonly={isReadonly}
                  viewerCanCreateIssues={secondaryData?.repository?.viewerCanCreateIssues ?? false}
                  viewerCanUpdate={data.viewerCanUpdateNext || false}
                  issueBodyTaskListAction={issueBodyTaskListAction}
                />
              </RefreshVideoWrapper>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={issueBodyStyles.outerContainer}>
      {!inlineAvatars && (
        <div className={`avatar-parent-child ${styles.avatarOuter} ${issueBodyStyles.avatarOuterWrapper}`}>
          <Link
            href={profileUrl ?? undefined}
            {...authorHovercardAttrs}
            aria-label={`@${login}'s profile`}
            className={profileUrl ? styles.avatarLink : styles.avatarWithoutLink}
          >
            <GitHubAvatar src={avatarUrl} size={40} alt={`@${login}`} className={styles.issueViewerAvatar} />
          </Link>
        </div>
      )}
      <div
        data-testid={TEST_IDS.issueBody}
        /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
        className={clsx('react-issue-body', issueBodyStyles.innerContainer, highlighted && issueBodyStyles.highlighted)}
        {...hpcAttrs()}
      >
        <h2 className="sr-only">{LABELS.issueBodyHeader}</h2>
        <div className={issueBodyStyles.headerRow}>
          <div
            className={clsx(issueBodyStyles.commentBorder, data.viewerDidAuthor && issueBodyStyles.commentBorderAuthor)}
          >
            {defferedIsEditing || isSubmitting || hasValidationError ? (
              <>
                <IssueBodyHeader
                  comment={data}
                  url={url}
                  secondaryKey={secondaryKey}
                  secondaryKeyIssue={secondaryKey}
                  authorHovercardAttrs={authorHovercardAttrs}
                />
                <div className={issueBodyStyles.editorWrapper}>
                  <Suspense fallback={null}>
                    <IssueBodyEditor
                      editorDisabled={isSubmitting}
                      trailingIcon={!isSubmitting}
                      subjectId={issueId}
                      subject={subject}
                      body={hasValidationError ? markdownBody : presavedBody || markdownBody}
                      bodyIsStale={bodyIsStale}
                      onChange={onBodyChange}
                      onCancel={() => {
                        setIsIssueBodyEditActive(false)
                        setMarkdownBody(issueBody)
                        setPresavedBody(undefined)
                        onIssueEditStateChange?.(false)
                        setCurrentIssueBodyVersion(bodyVersion)
                      }}
                      onCommit={() => {
                        commitIssueBodyEdit()
                      }}
                      repoOwner={data.repository.owner.login}
                      repoName={data.repository.name}
                      title={data.title}
                      shouldShowCopilotFunctionality={shouldShowCopilotFunctionality}
                    />
                  </Suspense>
                </div>
              </>
            ) : (
              <>
                <IssueBodyHeader
                  comment={data}
                  url={url}
                  actionProps={
                    isReadonly
                      ? undefined
                      : {
                          viewerCanUpdate: data.viewerCanUpdateNext || false,
                          startIssueBodyEdit: () => {
                            setIsIssueBodyEditActive(true)
                          },
                          onEditHover: IssueBodyEditor.preload,
                          url,
                          issueBodyRef,
                          onReplySelect: handleOnReply,
                          viewerCanReport: secondaryData?.viewerCanReport ?? false,
                          viewerCanReportToMaintainer: secondaryData?.viewerCanReportToMaintainer ?? false,
                          issueId: data.id,
                          owner: data.repository.owner.login,
                          ownerId: data.repository.owner.id,
                          ownerUrl: data.repository.owner.url,
                          viewerCanBlockFromOrg: secondaryData?.viewerCanBlockFromOrg ?? false,
                          viewerCanUnblockFromOrg: secondaryData?.viewerCanUnblockFromOrg ?? false,
                          viewerCanBlockPersonally: secondaryData?.viewerCanBlockPersonally ?? false,
                          viewerCanUnblockPersonally: secondaryData?.viewerCanUnblockPersonally ?? false,
                          pendingBlock: data.pendingBlock ?? undefined,
                          pendingUnblock: data.pendingUnblock ?? undefined,
                          isDisabled: hasDisabledActions,
                          actionMenuPortalContainerName: insideSidePanel ? SIDE_PANEL_PORTAL_ROOT : undefined,
                        }
                  }
                  secondaryKey={secondaryKey}
                  secondaryKeyIssue={secondaryKey}
                  authorHovercardAttrs={authorHovercardAttrs}
                />
                <RefreshVideoWrapper<IssueBodyRefetchQuery>
                  /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point. */
                  bodyHTML={bodyHTML as SafeHTMLString}
                  bodyRef={issueBodyRef}
                  id={issueId}
                  query={IssueBodyRefetchQuery}
                  getHTML={getHTML}
                >
                  <IssueBodyViewer
                    /* eslint-disable-next-line @github-ui/github-monorepo/no-cast-as-safe-html-string -- HTML is sanitized server-side. TODO: Move type assertion to data ingress point. */
                    html={bodyHTML as SafeHTMLString}
                    markdown={issueBody}
                    comment={data}
                    onLinkClick={onLinkClick}
                    issueBodyRef={issueBodyRef}
                    bodyVersion={currentIssueBodyVersion}
                    locked={data.locked}
                    reactable={data}
                    subIssues={data}
                    pinnedComment={data}
                    pinnedCommentSecondary={secondaryData}
                    insideSidePanel={insideSidePanel}
                    repositoryId={data.repository.id}
                    onIssueEditStateChange={onIssueEditStateChange}
                    isReadonly={isReadonly}
                    viewerCanCreateIssues={secondaryData?.repository?.viewerCanCreateIssues ?? false}
                    viewerCanUpdate={data.viewerCanUpdateNext || false}
                    issueBodyTaskListAction={issueBodyTaskListAction}
                  />
                </RefreshVideoWrapper>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
